import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from './cn.ts';

/**
 * shadcn's button, on the kit's tokens: red only for the one action that
 * moves the page on (`primary`), a white box for the rest, and words-only
 * `quiet`. Not a pill: controls are 10 px boxes. Icons inside are 20 px.
 */
export const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center gap-s2 rounded-control border text-body font-semibold whitespace-nowrap transition-colors select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-default disabled:opacity-60 print:hidden [&_svg]:size-icon [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'border-transparent bg-brand text-on-brand hover:bg-brand-press',
        secondary: 'border-line-strong bg-surface text-ink hover:bg-sunk',
        quiet: 'border-transparent bg-transparent text-brand hover:bg-brand-soft',
        danger: 'border-line-strong bg-surface text-late-ink hover:bg-late-soft',
        ghost: 'border-transparent bg-transparent text-ink-2 hover:bg-sunk hover:text-ink',
      },
      size: {
        default: 'h-control min-w-control px-s4',
        icon: 'size-control',
        /** The one action a screen is for (a station's press): the main control height, larger words. */
        main: 'h-main min-w-control px-s6 text-strong',
        /** A trigger holding an avatar and words: the control's height, tight sides. */
        compact: 'h-control px-s2',
      },
      wide: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'default', wide: false },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

export function UiButton({ className, variant, size, wide, asChild = false, ...props }: ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { readonly asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'button';
  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, wide }), className)} {...props} />;
}
